import { useState, useEffect } from "react";
import "./BasicTable.css";
import { CircularProgress, Fade } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import divisionService from "../../services/divisionService";
import { formatDate } from "../../utils/helpers";

export default function BasicTable({ events = [], selectedDateFilter, onDateFilterChange }) {
  const [loading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEvents, setFilteredEvents] = useState(events);
  const [divisionNames, setDivisionNames] = useState({});

  useEffect(() => {
    // Fetch division names for all unique division IDs
    const fetchDivisionNames = async () => {
      const uniqueDivisionIds = [...new Set(events.map(event => event.division_id))];
      const names = {};
      
      for (const divisionId of uniqueDivisionIds) {
        if (divisionId) {
          try {
            const name = await divisionService.getNameById(divisionId);
            names[divisionId] = name;
          } catch (error) {
            console.error(`Error fetching division name for ID ${divisionId}:`, error);
            names[divisionId] = 'N/A';
          }
        }
      }
      
      setDivisionNames(names);
    };

    if (events.length > 0) {
      fetchDivisionNames();
    }
  }, [events]);

  useEffect(() => {
    // Filter events based on search query
    const filtered = events.filter(event => {
      const searchLower = searchQuery.toLowerCase();
      const divisionName = divisionNames[event.division_id] || 'N/A';
      return (
        event.id?.toLowerCase().includes(searchLower) ||
        event.name?.toLowerCase().includes(searchLower) ||
        divisionName.toLowerCase().includes(searchLower) 
        
      );
    });
    setFilteredEvents(filtered);
  }, [events, searchQuery, divisionNames]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleDateChange = (e) => {
    onDateFilterChange(e.target.value);
  };

  return (
    <Fade in={true} timeout={500}>
      <div className="bg-white p-4 rounded-lg">
        <div className="flex justify-end mb-6 gap-2 flex-wrap">
          <input 
            className="border border-slate-200 rounded p-2" 
            placeholder="Search events..." 
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <FormControl sx={{ minWidth: 120 }} size="small">
            <Select
              value={selectedDateFilter}
              onChange={handleDateChange}
              className="bg-white"
            >
              <MenuItem value="This Day">This Day</MenuItem>
              <MenuItem value="This Week">This Week</MenuItem>
              <MenuItem value="This Month">This Month</MenuItem>
            </Select>
          </FormControl>
        </div>

        <div className="overflow-x-auto mt-4 mb-6 relative min-h-[400px]">
          {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <CircularProgress />
            </div>
          ) : (
            <div className="min-w-full inline-block align-middle">
              <div className="overflow-hidden">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">ID</th>
                      <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">Event Name</th>
                      <th className="hidden md:table-cell px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">Division</th>
                      <th className="px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">Start At</th>
                      <th className="hidden md:table-cell px-3 py-3 text-left text-xs md:text-sm font-semibold text-gray-900">End Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredEvents.map((event) => (
                      <tr key={event.id} className="hover:bg-gray-50">
                        <td className="px-3 py-4 text-sm text-gray-900">{event.id}</td>
                        <td className="px-3 py-4 text-sm text-gray-900">{event.name}</td>
                        <td className="hidden md:table-cell px-3 py-4 text-sm text-gray-900">
                          {divisionNames[event.division_id] || 'N/A'}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-900">
                          {formatDate(event.start_date)}
                        </td>
                        <td className="hidden md:table-cell px-3 py-4 text-sm text-gray-900">
                          {formatDate(event.end_date)}
                        </td>
                      </tr>
                    ))}
                    {filteredEvents.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-3 py-4 text-sm text-center text-gray-500">
                          {searchQuery ? 'No events match your search' : 'No events found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fade>
  );
}